import { useRouter } from "next/router";
import { useQuery } from "urql";
import { QUERY_ENDPOINTS } from "@/framework/utils/query-endpoints";
import React, { useState, useEffect } from "react";
import { InView } from "react-intersection-observer";
import Alert from "@/components/ui/alert";
import ProjectCard from "./project-card";
import { sanitize } from "@/framework/utils/sanitize";

export const ProjectsGrid = ({ className = "", activeTag }) => {
  const router = useRouter();
  const [fullyLoaded, setFullyLoaded] = useState(false);
  const [items, setItems] = useState([]);
  const [loadMoreData, setLoadMoreData] = useState(false);
  const [params, setParams] = useState({
    start: 0,
    limit: 10,
    sortBy: ["order:asc"],
    filter: {
      publishedAt: { ne: null },
    },
  });

  useEffect(() => {
    setItems([]);
    setFullyLoaded(false);
  }, []);

  //handle tag change
  useEffect(() => {
    setItems([]);
    if (activeTag === "All Projects") {
      setParams((prevParams) => ({
        ...prevParams,
        start: 0,
        limit: 10,
        filter: {
          publishedAt: { ne: null },
        },
      }));
    } else {
      setParams((prevParams) => ({
        ...prevParams,
        start: 0,
        limit: 10,
        filter: {
          publishedAt: { ne: null },
          project_tags: { name: { eq: activeTag } },
        },
      }));
    }
  }, [activeTag]);

  const [{ data, fetching: loading, error }] = useQuery({
    query: QUERY_ENDPOINTS.GET_PROJECTS,
    variables: params,
    pause: !activeTag,
  });

  useEffect(() => {
    if (data) {
      const newItems = data?.projects?.data;
      if (!loadMoreData) {
        setItems(newItems);
      } else {
        setItems((prevItems) => [...prevItems, ...newItems]);
        setLoadMoreData(false);
      }
    }
  }, [data]);

  //fetch more logic
  const handleLoadMore = () => {
    setLoadMoreData(true);
    setParams((prevParams) => ({
      ...prevParams,
      start: items?.length,
      limit: 10,
    }));
  };

  return (
    <>
      {error ? (
        <div className="col-span-full">
          <Alert message={error?.message} className={"w-full"} />
        </div>
      ) : (
        <div
          className={`w-full grid auto-rows-auto grid-cols-1 lg:gap-6 md:gap-2 gap-4 ${className}`}
        >
          {items?.map((project, index) => {
            // Ensure that there are at least 5 elements before applying alternate layouts
            if (items.length >= 5) {
              // First layout block for items (apply this for index 0, 10, 20, etc.)
              if (index % 10 === 0 || (items.length === 10 && index === 0)) {
                return (
                  <div
                    className="grid md:grid-cols-4 md:grid-rows-2 grid-cols-1 auto-rows-auto lg:gap-6 md:gap-2 gap-4 w-full"
                    key={"block" + index}
                  >
                    {[0, 1, 2, 3, 4].map(
                      (i) =>
                        items[index + i] && (
                          <ProjectCard
                            project={sanitize(items[index + i])}
                            key={`project-${index + i}`}
                            className={
                              i === 2
                                ? "md:col-span-2 md:row-span-2 md:h-full h-[20vw]"
                                : "lg:h-[20vw] md:h-[200px] h-[20vw]"
                            }
                          />
                        )
                    )}
                  </div>
                );
              }

              // Second layout block for items (apply this for index 5, 15, 25, etc.)
              if (index % 10 === 5 || (items.length === 10 && index === 5)) {
                return (
                  <div
                    className="grid md:grid-cols-4 md:grid-rows-2 grid-cols-1 auto-rows-auto lg:gap-6 md:gap-2 gap-4 w-full"
                    key={"block" + index}
                  >
                    {[0, 1, 2, 3, 4].map(
                      (i) =>
                        items[index + i] && (
                          <ProjectCard
                            project={sanitize(items[index + i])}
                            key={`project-${index + i}`}
                            className={
                              i === 0
                                ? "md:col-span-2 md:row-span-2 md:h-full h-[20vw]"
                                : "lg:h-[20vw] md:h-[200px] h-[20vw]"
                            }
                          />
                        )
                    )}
                  </div>
                );
              }
            }
            return null;
          })}
        </div>
      )}

      {!loading && (
        <InView
          onChange={async (inView) => {
            if (inView) {
              const result = await handleLoadMore();
              if (data?.projects?.data?.length === 0) {
                setFullyLoaded(true);
              } else {
                setFullyLoaded(false);
              }
            }
          }}
        />
      )}
    </>
  );
};
